/**
 * @fileoverview ProtectedRoute - Global authentication route guard
 * 
 * Wraps protected routes to enforce authentication. Redirects unauthenticated
 * users to /auth while preserving their intended destination. Also handles
 * session persistence for path restoration on return.
 * 
 * @module components/ProtectedRoute
 */

import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useOnboardingStatus } from "@/hooks/useOnboardingStatus";
import { useAuthSession } from "@/hooks/useAuthSession";
import { useEffect } from "react";
import { storeVisitedPath, cleanupInvalidStoredPaths } from "@/lib/session-persistence";

export function ProtectedRoute() {
  const { data: session, isLoading: sessionLoading } = useAuthSession();
  // Pass userId directly - both hooks now fetch in parallel instead of sequentially
  const userId = session?.user?.id;
  const { status, loading: statusLoading } = useOnboardingStatus(userId);
  const location = useLocation();

  // Combined loading state - but both are fetching simultaneously now
  const isLoading = sessionLoading || (userId && statusLoading);

  // Remember last path for session persistence and cleanup invalid entries
  useEffect(() => {
    if (session?.user) {
      // Clean up invalid/expired paths on mount
      cleanupInvalidStoredPaths(session.user.id);
    }

    if (status === "completed" && session?.user && location.pathname !== "/auth") {
      const currentMode = location.pathname.startsWith("/ats") || location.pathname.startsWith("/employer") ? "employer" : "personal";
      storeVisitedPath(session.user.id, location.pathname, currentMode);
    }
  }, [location.pathname, status, session]);

  // ALWAYS wait for loading to complete before making ANY redirect decisions
  if (isLoading) {
    return (
      <div className="fixed top-4 right-4 z-50">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  // Only redirect to auth after loading completes AND session is confirmed null
  if (!session) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  // Skip onboarding - go straight to dashboard (simplified auth flow)
  return <Outlet />;
}
